import React, { useEffect, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useProgram } from '../hooks/useProgram';
import { sendTx } from '../utils/transactions';
import {
  createUpdateRewardsInstruction,
  createWithdrawFeesInstruction,
  createInitializePoolInstruction,
  derivePoolPda,
  createSyncGlobalRewardsInstruction,
  createInitializePoolFeeInstruction,
} from '../utils/instructions';
import { PublicKey } from '@solana/web3.js';
import { ADMIN_PUBKEY, MONOPOLY_MINT } from '../constants';
import { getAccount, getAssociatedTokenAddress } from '@solana/spl-token';
import { formatTokenBalance, parseSmallestAmount } from '../utils/format';
import InputWithMax from './InputWithMax';

export default function AdminPanel({ poolState, refreshPool }: { poolState: any, refreshPool: () => void }) {
  const [newRewardRate, setNewRewardRate] = useState('');
  const [newFee, setNewFee] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');

  const [initialRewardRate, setInitialRewardRate] = useState('');
  const [initialFeePercentage, setInitialFeePercentage] = useState('');

  const { publicKey, signTransaction } = useWallet();
  const { connection, monopolyMint, rollMint } = useProgram();

  const [poolPda] = derivePoolPda(monopolyMint, rollMint);

  const [feeVaultBalance, setFeeVaultBalance] = useState<bigint | null>(null);

  // Assume ROLL token has 9 decimals. Adjust if different.
  const ROLL_DECIMALS = 9;

  /**
   * Convert a floating-point ROLL/s rate into the smallest units per second as a u64.
   * For example: 0.0000115 ROLL/s -> 0.0000115 * 10^9 = 11500 smallest units per second.
   */
  function convertRateToSmallestUnits(rateStr: string): number | null {
    if (!rateStr.trim()) return null;
    const rateFloat = parseFloat(rateStr);
    if (isNaN(rateFloat) || rateFloat < 0) return null;
    const scaled = Math.floor(rateFloat * Math.pow(10, ROLL_DECIMALS));
    return scaled;
  }

  async function handleUpdateRewards() {
    if (!publicKey || !signTransaction) {
      alert('Admin wallet not connected');
      return;
    }

    let newRewardRateOption: number | null = null;
    if (newRewardRate.trim()) {
      const scaledRate = convertRateToSmallestUnits(newRewardRate);
      if (scaledRate === null) {
        alert('Invalid reward rate');
        return;
      }
      newRewardRateOption = scaledRate;
    }

    let newFeeOption: number | null = null;
    if (newFee.trim()) {
      const feeValue = Number(newFee);
      if (isNaN(feeValue) || feeValue < 0 || feeValue > 15) {
        alert('Invalid fee percentage. Must be between 0 and 15.');
        return;
      }
      newFeeOption = feeValue;
    }

    const ix = createUpdateRewardsInstruction(
      publicKey,
      poolPda,
      newRewardRateOption,
      newFeeOption
    );

    try {
      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      alert('Rewards updated successfully.');
      refreshPool();
    } catch (err: any) {
      console.error(err);
      alert('Failed to update rewards: ' + err.message);
    }
  }

  async function handleWithdrawFees() {
    if (!publicKey || !signTransaction) {
      alert('Admin wallet not connected');
      return;
    }

    if (!poolState) {
      alert('Pool not initialized');
      return;
    }

    const amount = Number(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Invalid amount');
      return;
    }

    const smallestAmount = parseSmallestAmount(withdrawAmount, 6);

    const adminMonopolyAccount = await getAssociatedTokenAddress(
      new PublicKey(MONOPOLY_MINT),
      new PublicKey(ADMIN_PUBKEY),
    );

    const ix = createWithdrawFeesInstruction(
      publicKey,
      poolPda,
      new PublicKey(poolState.monopoly_fee_vault),
      adminMonopolyAccount,
      smallestAmount,
    );

    try {
      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      alert(`Withdrawn ${amount} from fee vault.`);
      refreshPool();
    } catch (err: any) {
      console.error(err);
      alert('Failed to withdraw fees: ' + err.message);
    }
  }

  async function handleSyncGlobalRewards() {
    if (!publicKey || !signTransaction) {
      alert('Admin wallet not connected');
      return;
    }

    if (!poolState) {
      alert('Pool not initialized');
      return;
    }

    const ix = createSyncGlobalRewardsInstruction(
      publicKey,
      poolPda,
    );

    try {
      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      alert('synced');
      refreshPool();
    } catch (err: any) {
      console.error(err);
      alert('Failed to sync: ' + err.message);
    }
  }

  async function handleInitializePool() {
    if (!publicKey || !signTransaction) {
      alert('Admin wallet not connected');
      return;
    }

    // Convert initial reward rate
    const initRateFloat = parseFloat(initialRewardRate);
    if (isNaN(initRateFloat) || initRateFloat < 0) {
      alert('Invalid initial reward rate');
      return;
    }
    const initScaledRate = Math.floor(initRateFloat * Math.pow(10, ROLL_DECIMALS));

    const initFee = Number(initialFeePercentage);
    if (isNaN(initFee) || initFee < 0 || initFee > 15) {
      alert('Invalid initial fee percentage, must be 0 to 15');
      return;
    }

    const ix = createInitializePoolInstruction(
      publicKey,
      monopolyMint,
      rollMint,
      poolPda,
      initScaledRate,
      initFee
    );

    try {
      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      alert('Pool initialized successfully.');
      refreshPool();
    } catch (err: any) {
      console.error(err);
      alert('Failed to initialize pool: ' + err.message);
    }
  }

  async function handleInitializePoolFee() {
    if (!publicKey || !signTransaction) {
      alert('Admin wallet not connected');
      return;
    }

    const ix = createInitializePoolFeeInstruction(
      publicKey,
      monopolyMint,
      poolPda,
    );

    try {
      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      alert('Pool initialized successfully.');
      refreshPool();
    } catch (err: any) {
      console.error(err);
      alert('Failed to initialize pool: ' + err.message);
    }
  }

  // Fetch fee vault balance
  useEffect(() => {
    async function fetchFeeVaultBalance() {
      if (!poolState?.monopoly_fee_vault) return;
      try {
        const feeVaultAccount = await getAccount(connection, new PublicKey(poolState.monopoly_fee_vault));
        setFeeVaultBalance(feeVaultAccount.amount);
      } catch (err) {
        console.error('Error fetching fee vault balance:', err);
        setFeeVaultBalance(null);
      }
    }
    fetchFeeVaultBalance();
  }, [poolState, connection]);

  const handleMaxWithdraw = () => {
    if (feeVaultBalance !== null) {
      setWithdrawAmount(formatTokenBalance(feeVaultBalance, 6, 1));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded p-4 shadow mt-4">
      <h2 className="text-lg font-bold">Admin Panel</h2>

      {/* If poolState is null, show initialize form */}
      {!poolState && (
        <div className="mt-4 p-4 border">
          <h3 className="font-bold mb-2">Initialize Pool</h3>
          <div className="mb-2">
            <label className="block">Initial Reward Rate (in ROLL/s, e.g. 0.0000115): </label>
            <input
              type="text"
              className="border p-1 text-black"
              value={initialRewardRate}
              onChange={e => setInitialRewardRate(e.target.value)}
            />
          </div>
          <div className="mb-2">
            <label className="block">Initial Fee Percentage (0-15): </label>
            <input
              type="text"
              className="border p-1 text-black"
              value={initialFeePercentage}
              onChange={e => setInitialFeePercentage(e.target.value)}
            />
          </div>
          <button className="px-4 py-1 bg-green-500 text-white rounded" onClick={handleInitializePool}>Initialize Pool</button>
        </div>
      )}

      {poolState && (
        <>
          <div className="mt-4">
            <h3 className="font-bold">Update Reward Rate & Fee</h3>
            <div className="mb-2">
              <button className="px-4 py-1 bg-green-500 text-white rounded" onClick={handleInitializePoolFee}>Initialize Pool Fee</button>
              <label className="block">New Reward Rate (ROLL/s, can be very small):</label>
              <input
                type="text"
                className="border p-1 mr-2"
                placeholder="e.g. 0.0000115"
                value={newRewardRate}
                onChange={e => setNewRewardRate(e.target.value)}
              />
            </div>
            <div className="mb-2">
              <label className="block">New Fee % (0-15):</label>
              <input
                type="text"
                className="border p-1 mr-2"
                placeholder="e.g. 5"
                value={newFee}
                onChange={e => setNewFee(e.target.value)}
              />
            </div>
            <button className="px-4 py-1 bg-blue-500 text-white rounded" onClick={handleUpdateRewards}>Update</button>
          </div>

          <div className="mt-4">
            <h3 className="font-bold">Withdraw Fees</h3>
            <div className="mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Fee Vault Balance: {feeVaultBalance !== null ? formatTokenBalance(feeVaultBalance, 6, 1) : 'Loading...'} MONOPOLY
              </p>
              <div className="flex items-center gap-2">
                <InputWithMax
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="Amount to withdraw"
                  onMaxClick={handleMaxWithdraw}
                />
                <button className="px-4 py-1 bg-red-500 text-white rounded" onClick={handleWithdrawFees}>
                  Withdraw
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <h3 className="font-bold">Sync Global Rewards</h3>
            <button className="px-4 py-1 bg-red-500 text-white rounded" onClick={handleSyncGlobalRewards}>Sync Global Rewards</button>
          </div>
        </>
      )}
    </div>
  );
}

